export const VISUAL_DELIVERY_TARGET_FPS = 5;
export const VISUAL_DELIVERY_FRAME_INTERVAL_MS =
  1_000 / VISUAL_DELIVERY_TARGET_FPS;
export const VISUAL_DELIVERY_FRAME_WIDTH = 320;
export const VISUAL_DELIVERY_FRAME_HEIGHT = 240;

export type VisualDeliverySummary = {
  framesAnalyzed: number;
  observedSeconds: number;
  faceVisiblePercent: number;
  wellFramedPercent: number;
  cameraFacingPercent: number;
  uprightPosturePercent: number;
  averageHeadMovement: "low" | "moderate" | "high" | "not enough data";
  excludedFromInterviewScore: true;
};

export type VisualDeliveryAggregate = {
  framesAnalyzed: number;
  faceVisibleFrames: number;
  wellFramedFrames: number;
  cameraFacingFrames: number;
  uprightPostureFrames: number;
  headMovementSamples: number;
  normalizedHeadMovementTotal: number;
  firstTimestampMs: number | null;
  lastTimestampMs: number | null;
};

export type VisualDeliveryLandmark = {
  x: number;
  y: number;
  visibility?: number;
};

export type VisualDeliveryPerformanceState = {
  intervalMs: number;
  slowFrameStrikes: number;
  disabled: boolean;
};

export const EMPTY_VISUAL_DELIVERY_SUMMARY: VisualDeliverySummary = {
  framesAnalyzed: 0,
  observedSeconds: 0,
  faceVisiblePercent: 0,
  wellFramedPercent: 0,
  cameraFacingPercent: 0,
  uprightPosturePercent: 0,
  averageHeadMovement: "not enough data",
  excludedFromInterviewScore: true,
};

export function createVisualDeliveryAggregate(): VisualDeliveryAggregate {
  return {
    framesAnalyzed: 0,
    faceVisibleFrames: 0,
    wellFramedFrames: 0,
    cameraFacingFrames: 0,
    uprightPostureFrames: 0,
    headMovementSamples: 0,
    normalizedHeadMovementTotal: 0,
    firstTimestampMs: null,
    lastTimestampMs: null,
  };
}

const visible = (landmark: VisualDeliveryLandmark | undefined, minimum = 0.45) =>
  Boolean(
    landmark &&
      Number.isFinite(landmark.x) &&
      Number.isFinite(landmark.y) &&
      (landmark.visibility ?? 1) >= minimum,
  );

const clampPercent = (value: number) =>
  Math.max(0, Math.min(100, Math.round(value)));

export function addPoseMeasurement(
  aggregate: VisualDeliveryAggregate,
  landmarks: VisualDeliveryLandmark[] | undefined,
  timestampMs: number,
  previousNose: VisualDeliveryLandmark | null,
): VisualDeliveryLandmark | null {
  aggregate.framesAnalyzed += 1;
  aggregate.firstTimestampMs ??= timestampMs;
  aggregate.lastTimestampMs = timestampMs;

  if (!landmarks?.length) return null;

  // MediaPipe Pose indices: nose 0, eyes 2/5, ears 7/8, shoulders 11/12.
  const nose = landmarks[0];
  const leftEye = landmarks[2];
  const rightEye = landmarks[5];
  const leftEar = landmarks[7];
  const rightEar = landmarks[8];
  const leftShoulder = landmarks[11];
  const rightShoulder = landmarks[12];
  const faceVisible =
    visible(nose) && visible(leftEye) && visible(rightEye) && visible(leftShoulder) && visible(rightShoulder);

  if (!faceVisible) return null;
  aggregate.faceVisibleFrames += 1;

  const shoulderWidth = Math.max(
    0.001,
    Math.hypot(rightShoulder.x - leftShoulder.x, rightShoulder.y - leftShoulder.y),
  );
  const shoulderMidX = (leftShoulder.x + rightShoulder.x) / 2;
  const shoulderMidY = (leftShoulder.y + rightShoulder.y) / 2;
  const headCentered = Math.abs(nose.x - 0.5) <= 0.2;
  const headHeightGood = nose.y >= 0.12 && nose.y <= 0.48;
  const shoulderSizeGood = shoulderWidth >= 0.16 && shoulderWidth <= 0.72;
  const shouldersInFrame = shoulderMidY >= 0.34 && shoulderMidY <= 0.82;
  if (headCentered && headHeightGood && shoulderSizeGood && shouldersInFrame) {
    aggregate.wellFramedFrames += 1;
  }

  const leftEyeDistance = Math.abs(nose.x - leftEye.x);
  const rightEyeDistance = Math.abs(rightEye.x - nose.x);
  const eyeSymmetry =
    Math.min(leftEyeDistance, rightEyeDistance) /
    Math.max(0.001, Math.max(leftEyeDistance, rightEyeDistance));
  const earsVisible = visible(leftEar, 0.3) && visible(rightEar, 0.3);
  const noseNearEyeMidpoint =
    Math.abs(nose.x - (leftEye.x + rightEye.x) / 2) <= shoulderWidth * 0.18;
  if (eyeSymmetry >= 0.48 && noseNearEyeMidpoint && earsVisible) {
    aggregate.cameraFacingFrames += 1;
  }

  const shoulderTilt = Math.abs(leftShoulder.y - rightShoulder.y) / shoulderWidth;
  const headOverShoulders = Math.abs(nose.x - shoulderMidX) / shoulderWidth;
  if (shoulderTilt <= 0.15 && headOverShoulders <= 0.38) {
    aggregate.uprightPostureFrames += 1;
  }

  if (previousNose && visible(previousNose)) {
    aggregate.headMovementSamples += 1;
    aggregate.normalizedHeadMovementTotal += Math.min(
      1,
      Math.hypot(nose.x - previousNose.x, nose.y - previousNose.y) / shoulderWidth,
    );
  }

  return { x: nose.x, y: nose.y, visibility: nose.visibility };
}

export function summarizeVisualDelivery(
  aggregate: VisualDeliveryAggregate,
): VisualDeliverySummary {
  const visibleFrames = Math.max(1, aggregate.faceVisibleFrames);
  const observedMs =
    aggregate.firstTimestampMs === null || aggregate.lastTimestampMs === null
      ? 0
      : Math.max(0, aggregate.lastTimestampMs - aggregate.firstTimestampMs);
  const averageMovement = aggregate.headMovementSamples
    ? aggregate.normalizedHeadMovementTotal / aggregate.headMovementSamples
    : null;
  const averageHeadMovement =
    averageMovement === null
      ? "not enough data"
      : averageMovement < 0.025
        ? "low"
        : averageMovement <= 0.075
          ? "moderate"
          : "high";

  return {
    framesAnalyzed: aggregate.framesAnalyzed,
    observedSeconds: Math.round(observedMs / 100) / 10,
    faceVisiblePercent: clampPercent(
      (aggregate.faceVisibleFrames / Math.max(1, aggregate.framesAnalyzed)) * 100,
    ),
    wellFramedPercent: clampPercent(
      (aggregate.wellFramedFrames / visibleFrames) * 100,
    ),
    cameraFacingPercent: clampPercent(
      (aggregate.cameraFacingFrames / visibleFrames) * 100,
    ),
    uprightPosturePercent: clampPercent(
      (aggregate.uprightPostureFrames / visibleFrames) * 100,
    ),
    averageHeadMovement,
    excludedFromInterviewScore: true,
  };
}

export function nextVisualDeliveryPerformanceState(
  current: VisualDeliveryPerformanceState,
  processingMs: number,
): VisualDeliveryPerformanceState {
  if (!Number.isFinite(processingMs) || processingMs >= 500) {
    const slowFrameStrikes = current.slowFrameStrikes + 1;
    return {
      intervalMs: 1_000,
      slowFrameStrikes,
      disabled: slowFrameStrikes >= 3,
    };
  }
  if (processingMs >= 250) {
    return {
      intervalMs: 500,
      slowFrameStrikes: Math.min(2, current.slowFrameStrikes + 1),
      disabled: false,
    };
  }
  return {
    intervalMs: VISUAL_DELIVERY_FRAME_INTERVAL_MS,
    slowFrameStrikes: Math.max(0, current.slowFrameStrikes - 1),
    disabled: false,
  };
}

export function visualDeliveryCoachingTips(summary: VisualDeliverySummary): string[] {
  if (summary.framesAnalyzed < 10) {
    return ["Not enough unobstructed camera time was available for reliable visual coaching."];
  }
  const tips: string[] = [];
  if (summary.wellFramedPercent < 75) {
    tips.push("Center your head and shoulders and leave a little space above your head.");
  }
  if (summary.cameraFacingPercent < 65) {
    tips.push("Look toward the camera more often while answering, then glance away naturally when thinking.");
  }
  if (summary.uprightPosturePercent < 75) {
    tips.push("Keep your shoulders level and position your head comfortably above your shoulders.");
  }
  if (summary.averageHeadMovement === "high") {
    tips.push("Try slightly smaller head movements while keeping your delivery natural.");
  }
  if (!tips.length) {
    tips.push("Your framing, camera-facing time, and posture were consistent during the analyzed frames.");
  }
  return tips;
}
